import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useRefreshLimit() {
  const { user } = useAuth();
  const [refreshCount, setRefreshCount] = useState(0);
  const [canRefresh, setCanRefresh] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [plan, setPlan] = useState<"free" | "pro">("free");

  const getCurrentMonthYear = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  };

  const getExpirationDate = () => {
    const expiration = new Date();
    expiration.setMonth(expiration.getMonth() + 1);
    return expiration.toISOString();
  };

  const checkRefreshLimit = async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      {/* Check user subscription tier */}
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("subscription_tier")
        .eq("user_id", user.id)
        .single();

      if (profileError) throw profileError;

      if (profile?.subscription_tier === "pro") {
        setPlan("pro");
        setCanRefresh(true); // unlimited
        setRefreshCount(0);
        return;
      } else {
        setPlan("free");
      }

     {/* Check current month's refresh count for free users */}
      const monthYear = getCurrentMonthYear();
      const { data, error } = await supabase
        .from("chat_refreshes")
        .select("refresh_count")
        .eq("user_id", user.id)
        .eq("month_year", monthYear)
        .maybeSingle();

      if (error) throw error;

      const currentCount = data?.refresh_count || 0;
      setRefreshCount(currentCount);
      setCanRefresh(currentCount < 3); // Limit to 3 refreshes per month for free users
    } catch (error) {
      console.error("Error checking refresh limit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) checkRefreshLimit();
  }, [user]);

  const incrementRefreshCount = useCallback(async () => {
    if (!user || !canRefresh) return false;

    try {
      {/* Check subscription tier */}
      if (plan === "pro") {
        return true;
      }

      const monthYear = getCurrentMonthYear();

      const { data: existingRefresh, error: readError } = await supabase
        .from("chat_refreshes")
        .select("refresh_count")
        .eq("user_id", user.id)
        .eq("month_year", monthYear)
        .maybeSingle();

      if (readError) throw readError;

      const nextRefreshCount = (existingRefresh?.refresh_count || 0) + 1;
      const payload = {
        user_id: user.id,
        month_year: monthYear,
        refresh_count: nextRefreshCount,
        expires_at: getExpirationDate(),
      };

      const query = existingRefresh
        ? supabase
            .from("chat_refreshes")
            .update(payload)
            .eq("user_id", user.id)
            .eq("month_year", monthYear)
        : supabase
            .from("chat_refreshes")
            .insert(payload);

      const { data, error } = await query.select().single();

      if (error) throw error;

      const newCount = data.refresh_count;
      setRefreshCount(newCount);
      setCanRefresh(newCount < 3); // Limit to 3 refreshes per month for free users
      

      return true;
    } catch (error) {
      console.error("Error incrementing refresh count:", error);
      return false;
    }
  }, [user, refreshCount, canRefresh, plan]);

  return {
    plan,
    refreshCount,
    canRefresh,
    remainingRefreshes: plan === "pro" ? Infinity : Math.max(0, 3 - refreshCount),
    incrementRefreshCount,
    isLoading,
  };
}
